import React from 'react';
import { useTranslation } from 'react-i18next';

interface Solution {
  id: string;
  name: string;
  features?: string | null;
  claims?: string | null;
  fto_analysis?: string | null;
  patentability?: string | null;
  industrialization?: string | null;
  patent_application?: string | null;
}

interface SolutionProgressMatrixProps {
  solutions: Solution[];
  activeSolutionId: string | null;
  onSolutionClick?: (solutionId: string) => void;
}

const AGENTS = [
  { key: 'design', fields: ['features', 'claims'] },
  { key: 'fto', fields: ['fto_analysis'] },
  { key: 'patentability', fields: ['patentability'] },
  { key: 'patent_application', fields: ['patent_application'] },
  { key: 'industrialization', fields: ['industrialization'] },
] as const;

function getFieldStatus(solution: Solution, fields: readonly string[]): 'empty' | 'in-progress' {
  const filled = fields.filter(f => {
    const val = (solution as unknown as Record<string, string | undefined>)[f];
    return val && val.trim().length > 0;
  }).length;
  if (filled === 0) return 'empty';
  return 'in-progress';
}

const SolutionProgressMatrix: React.FC<SolutionProgressMatrixProps> = ({
  solutions,
  activeSolutionId,
  onSolutionClick,
}) => {
  const { t } = useTranslation();

  if (!solutions || solutions.length === 0) return null;

  return (
    <div className="text-xs border border-slate-200 rounded bg-white mb-2">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-50">
            <th className="px-2 py-1 text-left font-medium text-slate-600 border-b border-slate-200"></th>
            {AGENTS.map(a => (
              <th key={a.key} className="px-2 py-1 text-center font-medium text-slate-600 border-b border-slate-200">
                {t(`scv2.workflow.agents.${a.key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {solutions.map(sol => {
            const isActive = sol.id === activeSolutionId;
            return (
              <tr
                key={sol.id}
                className={`cursor-pointer hover:bg-slate-50 ${isActive ? 'bg-blue-50' : ''}`}
                onClick={() => onSolutionClick?.(sol.id)}
              >
                <td className="px-2 py-1 font-medium text-slate-700 border-b border-slate-100 whitespace-nowrap">
                  {sol.name}
                </td>
                {AGENTS.map(a => {
                  const status = getFieldStatus(sol, a.fields);
                  return (
                    <td key={a.key} className="px-2 py-1 text-center border-b border-slate-100">
                      {status === 'empty' && (
                        <span className="inline-block w-3 h-3 rounded-full bg-slate-200" title="Empty" />
                      )}
                      {status === 'in-progress' && (
                        <span className="inline-block w-3 h-3 rounded-full bg-amber-400" title="In progress" />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SolutionProgressMatrix;
